// IndexedDB para rascunhos offline manuais (BEL-1578 M1) + turnos offline do
// assistente de IA (BEL-1578 Etapa 2 / M-A). Os stores do assistente
// (assistant_turns/assistant_session) são aditivos — os guards if(!contains)
// preservam os stores/dados manuais existentes.
//
// IMPORTANTE: sempre que se ACRESCENTA um objectStore é OBRIGATÓRIO subir
// DB_VERSION. Senão o onupgradeneeded não corre para quem já tem a BD (store
// nunca criado → NotFoundError), e quem apanhou uma versão mais alta num build
// anterior leva VersionError ao abrir com a versão antiga. v3: regista os
// stores assistant_* que tinham sido adicionados sem subir a versão.
// v4: merge BEL-1609 (sem `kv`) + BEL-1549 (com `kv`) — ambos os ramos usaram v3
// com conjuntos de stores diferentes, por isso é OBRIGATÓRIO subir para 4 para
// que o onupgradeneeded corra em quem já abriu um v3 sem o store `kv`.
// v5: store assistant_review (cache read-only do /review — Fase 2 BEL-1671).
const DB_NAME = "bel_report_drafts";
const DB_VERSION = 5;

function openDb() {
	return new Promise((resolve, reject) => {
		const req = indexedDB.open(DB_NAME, DB_VERSION);
		req.onupgradeneeded = event => {
			const db = event.target.result;
			if (!db.objectStoreNames.contains("drafts")) {
				db.createObjectStore("drafts", { keyPath: "key" }); // key = `${visitId}:${reportType}`
			}
			if (!db.objectStoreNames.contains("photos")) {
				db.createObjectStore("photos", { keyPath: "photoUuid" });
			}
			if (!db.objectStoreNames.contains("sync_queue")) {
				db.createObjectStore("sync_queue", { keyPath: "id", autoIncrement: true });
			}
			// BEL-1578 Etapa 2 (M-A): stores do assistente offline. Aditivos —
			// o onupgradeneeded corre para qualquer salto de versão e os guards
			// if (!contains) acima preservam os stores/dados manuais existentes.
			if (!db.objectStoreNames.contains("assistant_turns")) {
				db.createObjectStore("assistant_turns", { keyPath: "turnUuid" });
			}
			if (!db.objectStoreNames.contains("assistant_session")) {
				db.createObjectStore("assistant_session", { keyPath: "visitId" });
			}
			// BEL-1549 M3: store key-value para a página relayar o token CSRF ao
			// Service Worker (que não tem acesso ao document). Aditivo.
			if (!db.objectStoreNames.contains("kv")) {
				db.createObjectStore("kv", { keyPath: "key" });
			}
			// BEL-1671 Fase 2: cache read-only do GET /review. Aditivo.
			if (!db.objectStoreNames.contains("assistant_review")) {
				db.createObjectStore("assistant_review", { keyPath: "visitId" });
			}
		};
		req.onsuccess = () => resolve(req.result);
		req.onerror = () => reject(req.error);
	});
}

function tx(db, store, mode) {
	return db.transaction(store, mode).objectStore(store);
}

function promisify(request) {
	return new Promise((resolve, reject) => {
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error);
	});
}

export const reportDraftDb = {
	async putDraft(record) {
		const db = await openDb();
		return promisify(tx(db, "drafts", "readwrite").put(record));
	},
	async getDraft(key) {
		const db = await openDb();
		return promisify(tx(db, "drafts", "readonly").get(key));
	},
	async deleteDraft(key) {
		const db = await openDb();
		return promisify(tx(db, "drafts", "readwrite").delete(key));
	},
	async putPhoto(record) {
		const db = await openDb();
		return promisify(tx(db, "photos", "readwrite").put(record));
	},
	async getPhoto(photoUuid) {
		const db = await openDb();
		return promisify(tx(db, "photos", "readonly").get(photoUuid));
	},
	async deletePhoto(photoUuid) {
		const db = await openDb();
		return promisify(tx(db, "photos", "readwrite").delete(photoUuid));
	},
	// assistant_turns (keyPath turnUuid): manifesto durável do turno offline do
	// assistente. Shape: { turnUuid, visitId, photoUuids[], text, geo, deviceMeta,
	// capturedAt, syncState, taskId?, userMessageId? }.
	// syncState ∈ {pending, syncing, confirmed, needs_attention, dead_letter}.
	async putTurn(record) {
		const db = await openDb();
		return promisify(tx(db, "assistant_turns", "readwrite").put(record));
	},
	async getTurn(turnUuid) {
		const db = await openDb();
		return promisify(tx(db, "assistant_turns", "readonly").get(turnUuid));
	},
	async allTurns() {
		const db = await openDb();
		return promisify(tx(db, "assistant_turns", "readonly").getAll());
	},
	async deleteTurn(turnUuid) {
		const db = await openDb();
		return promisify(tx(db, "assistant_turns", "readwrite").delete(turnUuid));
	},
	// assistant_session (keyPath visitId, guardado como String): snapshot read-only
	// do GET /session. Shape: { visitId, snapshot, pendingTaskIds[], cachedAt }.
	async putSessionSnapshot(record) {
		const db = await openDb();
		return promisify(tx(db, "assistant_session", "readwrite").put(record));
	},
	async getSessionSnapshot(visitId) {
		const db = await openDb();
		return promisify(tx(db, "assistant_session", "readonly").get(String(visitId)));
	},
	// Snapshot de TODAS as visitas no store assistant_session. Usado pelo purge
	// local por inatividade (autosync.purgeStaleLocal), que decide pela idade do
	// cachedAt de cada visita.
	async allSessionSnapshots() {
		const db = await openDb();
		return promisify(tx(db, "assistant_session", "readonly").getAll());
	},
	// Apaga o snapshot read-only de UMA visita (store assistant_session). Usado
	// pela eviction da cache de chat (concluir/finalizar a visita, manter só a
	// visita atual). Não toca em drafts/fotos/fila — têm o seu próprio ciclo.
	async deleteSessionSnapshot(visitId) {
		const db = await openDb();
		return promisify(tx(db, "assistant_session", "readwrite").delete(String(visitId)));
	},
	// assistant_review (keyPath visitId, String): cache read-only do GET /review.
	// Shape: { visitId, reviewData, cachedAt }. Fase 2 BEL-1671.
	async putReviewSnapshot(record) {
		const db = await openDb();
		return promisify(tx(db, "assistant_review", "readwrite").put(record));
	},
	async getReviewSnapshot(visitId) {
		const db = await openDb();
		return promisify(tx(db, "assistant_review", "readonly").get(String(visitId)));
	},
	async deleteReviewSnapshot(visitId) {
		const db = await openDb();
		return promisify(tx(db, "assistant_review", "readwrite").delete(String(visitId)));
	},
	async enqueue(op) {
		const db = await openDb();
		return promisify(tx(db, "sync_queue", "readwrite").add(op));
	},
	async allQueue() {
		const db = await openDb();
		return promisify(tx(db, "sync_queue", "readonly").getAll());
	},
	async dequeue(id) {
		const db = await openDb();
		return promisify(tx(db, "sync_queue", "readwrite").delete(id));
	},
	// Número de RASCUNHOS manuais por sincronizar — conta (visitId, reportType)
	// DISTINTOS entre as ops não-assistente, em vez do comprimento bruto da fila.
	// Sem isto, vários saves do mesmo relatório (offline) e as ops do assistente
	// inflavam o "Sincronizar rascunhos (N)" (ex.: mostrava 4 para 2 relatórios).
	async countPending() {
		const db = await openDb();
		const queue = await promisify(tx(db, "sync_queue", "readonly").getAll());
		const reports = new Set();
		for (const op of queue) {
			if (op && op.kind !== "assistant" && op.visitId != null && op.reportType != null) {
				reports.add(`${op.visitId}:${op.reportType}`);
			}
		}
		return reports.size;
	},
	// Remove qualquer draft_deposit pendente para (visitId, reportType) — usado
	// antes de enfileirar um novo, para haver no máximo UM por relatório (o
	// depósito lê sempre o rascunho atual, logo ops repetidas são redundantes).
	async dequeueDraftDeposit(visitId, reportType) {
		const db = await openDb();
		const v = String(visitId);
		return new Promise((resolve, reject) => {
			const t = db.transaction("sync_queue", "readwrite");
			t.oncomplete = () => resolve();
			t.onerror = () => reject(t.error);
			const cursorReq = t.objectStore("sync_queue").openCursor();
			cursorReq.onsuccess = event => {
				const cursor = event.target.result;
				if (!cursor) {
					return;
				}
				const op = cursor.value;
				if (op && op.type === "draft_deposit" && String(op.visitId) === v && op.reportType === reportType) {
					cursor.delete();
				}
				cursor.continue();
			};
		});
	},
	// Count only assistant_turns whose syncState is still in-flight. Used by
	// taskMixin.pendingOfflineCount so manual-draft sync_queue ops don't inflate
	// the assistant's "N a aguardar rede" banner (Fix BEL-1578).
	async countPendingTurns() {
		// dead_letter é o valor legacy de needs_attention (clientes antigos):
		// conta também, senão o badge esconde turnos falhados que ainda existem.
		const PENDING_STATES = ["pending", "syncing", "needs_attention", "dead_letter"];
		const turns = await this.allTurns();
		return turns.filter(t => PENDING_STATES.includes(t.syncState)).length;
	},
	// IDs de visita DISTINTOS referenciados em qualquer store (drafts/photos/
	// sync_queue/assistant_turns/assistant_session). Usado pelo varrimento de
	// visitas fechadas para encontrar sujeira deixada por visitas encerradas
	// offline, noutro dispositivo, ou antes do purge por visita existir.
	async allVisitIds() {
		const db = await openDb();
		const ids = new Set();
		const scan = store =>
			new Promise((resolve, reject) => {
				const cursorReq = tx(db, store, "readonly").openCursor();
				cursorReq.onsuccess = event => {
					const cursor = event.target.result;
					if (!cursor) {
						resolve();
						return;
					}
					const v = cursor.value && cursor.value.visitId;
					if (v != null) {
						ids.add(String(v));
					}
					cursor.continue();
				};
				cursorReq.onerror = () => reject(cursorReq.error);
			});
		for (const store of [
			"drafts",
			"photos",
			"sync_queue",
			"assistant_turns",
			"assistant_session",
			"assistant_review",
		]) {
			await scan(store);
		}
		return Array.from(ids);
	},
	// Apaga TODOS os registos (drafts/photos/sync_queue) de uma visita numa só
	// transação — usado ao encerrar a visita. Limpa também a sync_queue, senão um
	// draft_deposit pendente recriaria a linha no servidor após o encerramento.
	async deleteByVisit(visitId) {
		const db = await openDb();
		const target = String(visitId);
		return new Promise((resolve, reject) => {
			const stores = ["drafts", "photos", "sync_queue", "assistant_turns", "assistant_session", "assistant_review"];
			const t = db.transaction(stores, "readwrite");
			t.oncomplete = () => resolve();
			t.onerror = () => reject(t.error);
			t.onabort = () => reject(t.error);
			// assistant_session / assistant_review: a chave É o visitId → delete direto.
			t.objectStore("assistant_session").delete(target);
			t.objectStore("assistant_review").delete(target);
			// restantes: varrer por cursor e comparar value.visitId.
			["drafts", "photos", "sync_queue", "assistant_turns"].forEach(store => {
				const cursorReq = t.objectStore(store).openCursor();
				cursorReq.onsuccess = event => {
					const cursor = event.target.result;
					if (!cursor) {
						return;
					}
					if (cursor.value && String(cursor.value.visitId) === target) {
						cursor.delete();
					}
					cursor.continue();
				};
			});
		});
	},
	async dequeueByPhoto(photoUuid) {
		const db = await openDb();
		return new Promise((resolve, reject) => {
			const t = db.transaction("sync_queue", "readwrite");
			t.oncomplete = () => resolve();
			t.onerror = () => reject(t.error);
			const cursorReq = t.objectStore("sync_queue").openCursor();
			cursorReq.onsuccess = event => {
				const cursor = event.target.result;
				if (!cursor) {
					return;
				}
				if (cursor.value && cursor.value.photoUuid === photoUuid) {
					cursor.delete();
				}
				cursor.continue();
			};
		});
	},
	// Remove every queued op (photo_upload + assistant_turn) tagged with this
	// turnUuid. Used when the user cancels a LOCAL-ONLY (not-yet-synced) capture
	// so no stray op replays the turn on the next reconnect.
	async putKv(key, value) {
		const db = await openDb();
		return promisify(tx(db, "kv", "readwrite").put({ key, value }));
	},
	async getKv(key) {
		const db = await openDb();
		const rec = await promisify(tx(db, "kv", "readonly").get(key));
		return rec ? rec.value : null;
	},
	async dequeueByTurn(turnUuid) {
		const db = await openDb();
		return new Promise((resolve, reject) => {
			const t = db.transaction("sync_queue", "readwrite");
			t.oncomplete = () => resolve();
			t.onerror = () => reject(t.error);
			const cursorReq = t.objectStore("sync_queue").openCursor();
			cursorReq.onsuccess = event => {
				const cursor = event.target.result;
				if (!cursor) {
					return;
				}
				if (cursor.value && cursor.value.turnUuid === turnUuid) {
					cursor.delete();
				}
				cursor.continue();
			};
		});
	},
};
